import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Form } from 'antd';
import { useId } from 'react';
import { ClientForm, type ClientFormValues } from './ClientForm';
import { normaliseClientFormValues } from './client-form-values';

export const Route = createFileRoute('/console/$orgId/configuration/fleet/client-database/add')({
    staticData: {
        presentation: 'overlay',
        modules: ['fleet'],
        metadata: {
            title: 'New client',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ClientAdd />
        </PageController>
    );
}

function ClientAdd() {
    const formId = useId();
    const formDirty = useFormDirty();
    const { trpcUtils } = useTrpc();
    const { mutate, isPending } = trpc.fleetClientDb.createFleetClient.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<ClientFormValues>();

    return (
        <OverlayPage
            isDirty={formDirty.isDirty}
            title="New client"
            size="large"
            afterClose={() => {
                form.resetFields();
                void trpcUtils.fleetClientDb.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ requestClose }) => (
                <DrawerFooter onCancel={requestClose} formId={formId} okLoading={isPending} />
            )}
        >
            {({ onClose }) => (
                <ClientForm
                    form={form}
                    {...formDirty.formProps}
                    formId={formId}
                    mode="create"
                    authorityRequired
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(normaliseClientFormValues(values), {
                            onSuccess() {
                                message.success('Saved.');
                                // The record now exists, so the drawer closes even if the form was edited while the
                                // request was pending: another Save from this create form would make a duplicate.
                                onClose();
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        });
                    }}
                />
            )}
        </OverlayPage>
    );
}
